import {
	Box3,
	Box3Helper,
	Mesh,
	MeshStandardMaterial,
	Object3D,
	Vector3,
} from 'three';
import { ProjectRenderer } from '../ProjectRenderer';
import { RenderUtils } from '../utils/RenderUtils';
import {
	SerializedHandleManager,
	ThreeEvent,
	UserDataObjectType,
	UserDataProp,
} from '../types';
import { AssetsLib } from '../utils/AssetsLib';
import { AdjustableHeights, PostType } from '@/types/creator';
import { roundToThousandth } from '@/utils/utilities';

export class HandleManager {
	private assetsLib = AssetsLib.getInstance();
	private _originalHeights: AdjustableHeights = {
		handle: 0,
		upperEnding: 0,
		lowerEnding: 0,
		total: 0,
	};
	private _adjustableHeights: AdjustableHeights = {
		handle: 0,
		upperEnding: 0,
		lowerEnding: 0,
		total: 0,
	};
	private modelPath = '';

	originToYCenterConst = 0;

	model: Object3D | null = null;
	middlePost: Object3D | null = null;
	middleSegment: Object3D | null = null;
	handle: Mesh | null = null;
	originPoint: Mesh | null = null;
	upperEnding: Object3D | null = null;
	upperEndingAdjustable: Object3D | null = null;
	lowerEnding: Object3D | null = null;
	lowerEndingAdjustable: Object3D | null = null;
	objectsAvailableSpaceObjects: Object3D[] = [];
	objectsAvailableSpaceBox3 = new Box3();
	objectsAvailableSpaceBox3Helper: Box3Helper | null = null;
	handleBox3 = new Box3();
	handleBox3Helper: Box3Helper | null = null;

	constructor(private renderer: ProjectRenderer) {}

	private async setObjects() {
		const model = await this.assetsLib.getModel(this.modelPath);

		this.renderer.project.add(model);
		this.model = model;

		const {
			originPoint,
			upperPost,
			lowerPost,
			handle,
			middlePost,
			upperEnding,
			upperEndingAdjustable,
			lowerEnding,
			lowerEndingAdjustable,
			objectsAvailableSpaceObjects,
			measurementPoints,
		} = RenderUtils.getModelParts(model);

		if (!handle) throw new Error('MISSING_HANDLE');

		this.objectsAvailableSpaceObjects = objectsAvailableSpaceObjects;
		this.renderer.handleBuilder.measurementManager.initialize(
			measurementPoints
		);

		if (upperEnding) upperEnding.visible = false;
		if (lowerEnding) lowerEnding.visible = false;

		this.handle = handle;
		this.originPoint = originPoint;
		this.renderer.objectsManager.middlePostObject = middlePost;
		this.renderer.objectsManager.upperPostObject = upperPost;
		this.renderer.objectsManager.lowerPostObject = lowerPost;
		this.upperEnding = upperEnding;
		this.upperEndingAdjustable = upperEndingAdjustable;
		this.lowerEnding = lowerEnding;
		this.lowerEndingAdjustable = lowerEndingAdjustable;

		if (this.upperEndingAdjustable)
			this.upperEndingAdjustable.userData[UserDataProp.TARGET] =
				UserDataObjectType.UPPER_ENDING_ADJUSTABLE;

		if (this.lowerEndingAdjustable)
			this.lowerEndingAdjustable.userData[UserDataProp.TARGET] =
				UserDataObjectType.LOWER_ENDING_ADJUSTABLE;

		if (this.renderer.objectsManager.middlePostObject)
			this.renderer.objectsManager.middlePostObject.visible = false;

		this.setOriginToYCenterConst();
	}

	private emitHeightsToSlice(omit: (keyof AdjustableHeights)[] = []) {
		const payload = structuredClone(this.adjustableHeights);

		let key: keyof AdjustableHeights;
		for (key in payload) {
			if (omit.includes(key)) {
				delete payload[key];
				continue;
			}
			payload[key] = Math.round(payload[key] * 1000);
		}

		this.renderer.emitter.emit(ThreeEvent.SET_ADJUSTABLE_HEIGHTS, payload);
	}

	private emitObjectsPosYToSlice() {
		const objects = [
			...this.renderer.objectsManager.posts,
			...this.renderer.objectsManager.accessories,
			...this.renderer.objectsManager.ledsManager.ledPoints,
		];

		const payload = objects.map((object) => ({
			id: object.id,
			posY: Math.round((object.posY + this.originToYCenterConst) * 1000),
		}));
		this.renderer.emitter.emit(ThreeEvent.UPDATE_OBJECTS_POS_Y, payload);
	}

	async setModel(path: string) {
		this.modelPath = path;

		await this.setObjects();

		const box = new Box3().setFromObject(this.handle!);
		const size = new Vector3();
		box.getSize(size);

		this._originalHeights.handle = size.y;
		this._adjustableHeights.handle = size.y;

		if (this.upperEndingAdjustable) {
			box.setFromObject(this.upperEndingAdjustable);
			box.getSize(size);
			this._originalHeights.upperEnding = size.y;
			this._adjustableHeights.upperEnding = size.y;
			this.upperEndingAdjustable.userData[UserDataProp.TARGET] =
				UserDataObjectType.UPPER_ENDING_ADJUSTABLE;
		}

		if (this.lowerEndingAdjustable) {
			box.setFromObject(this.lowerEndingAdjustable);
			box.getSize(size);
			this._originalHeights.lowerEnding = size.y;
			this._adjustableHeights.lowerEnding = size.y;
			this.lowerEndingAdjustable.userData[UserDataProp.TARGET] =
				UserDataObjectType.LOWER_ENDING_ADJUSTABLE;
		}

		this.emitHeightsToSlice(['total']);

		if (this.renderer.objectsManager.upperPostObject)
			this.renderer.emitter.emit(ThreeEvent.ADD_POST, {
				type: PostType.UPPER,
				posY:
					roundToThousandth(
						this.renderer.objectsManager.upperPostObject.position.y
					) * 1000,
			});

		if (this.renderer.objectsManager.lowerPostObject)
			this.renderer.emitter.emit(ThreeEvent.ADD_POST, {
				type: PostType.LOWER,
				posY:
					roundToThousandth(
						this.renderer.objectsManager.lowerPostObject.position.y
					) * 1000,
			});

		this.renderer.handleBuilder.build();
		this.handleBox3.setFromObject(this.handle!, true);

		if (this.renderer.isDev) {
			(this.handle!.material as MeshStandardMaterial).transparent = true;
			(this.handle!.material as MeshStandardMaterial).opacity = 0.9;
		}

		this.handleBox3Helper = new Box3Helper(this.handleBox3, 0xff0000);
		this.objectsAvailableSpaceBox3Helper = new Box3Helper(
			this.objectsAvailableSpaceBox3,
			0xff00ff
		);

		if (this.renderer.isDev) {
			this.renderer.scene.add(this.handleBox3Helper);
			this.renderer.scene.add(this.objectsAvailableSpaceBox3Helper);
		}

		this.renderer.cameraController.centerCamera();
	}

	get originalHeights() {
		return this._originalHeights;
	}

	get adjustableHeights() {
		return this._adjustableHeights;
	}

	setOriginToYCenterConst() {
		if (!this.originPoint) return;

		const globalPosition = new Vector3();
		this.originPoint.getWorldPosition(globalPosition);
		const distanceToYCenter =
			globalPosition.y - this.renderer.project.position.y;
		this.originToYCenterConst = Math.abs(distanceToYCenter);

		this.emitObjectsPosYToSlice();
	}

	updateHeights(data: Partial<AdjustableHeights>) {
		Object.assign(this._adjustableHeights, data);
		this.renderer.handleBuilder.build();
	}

	setEndingVisibility({
		type,
		value,
	}: {
		type: 'upperEnding' | 'lowerEnding';
		value: boolean;
	}) {
		if (type === 'upperEnding' && this.upperEnding)
			this.upperEnding.visible = value;

		if (type === 'lowerEnding' && this.lowerEnding)
			this.lowerEnding.visible = value;

		this.renderer.handleBuilder.measurementManager.updateMeasurements();
	}

	setTotalHeight(value: number) {
		this._adjustableHeights.total = value;
		this.renderer.handleBuilder.measurementManager.updateMeasurements();
	}

	updateBoxes() {
		if (!this.handle) return;

		this.handleBox3.setFromObject(this.handle, true);
		this.objectsAvailableSpaceBox3.setFromObject(this.handle, true);
		for (const object of this.objectsAvailableSpaceObjects) {
			this.objectsAvailableSpaceBox3.expandByObject(object);
		}
	}

	clear() {
		this.model = null;
		this.handle = null;
		this.middleSegment = null;
		this.upperEndingAdjustable = null;
		this.lowerEndingAdjustable = null;

		this._originalHeights = {
			handle: 0,
			upperEnding: 0,
			lowerEnding: 0,
			total: 0,
		};
		this._adjustableHeights = structuredClone(this._originalHeights);

		this.handleBox3Helper?.removeFromParent();
		this.handleBox3Helper?.dispose();
	}

	export(): SerializedHandleManager {
		return {
			originToYCenterConst: this.originToYCenterConst,
			adjustableHeights: structuredClone(this._adjustableHeights),
			originalHeights: structuredClone(this._originalHeights),
			modelPath: this.modelPath,
		};
	}

	async load(data: SerializedHandleManager) {
		this._adjustableHeights = structuredClone(data.adjustableHeights);
		this._originalHeights = structuredClone(data.originalHeights);
		this.modelPath = data.modelPath;

		await this.setObjects();

		this.renderer.cameraController.centerCamera();

		this.renderer.handleBuilder.build();
		this.handleBox3.setFromObject(this.handle!, true);

		if (this.renderer.isDev) {
			(this.handle!.material as MeshStandardMaterial).transparent = true;
			(this.handle!.material as MeshStandardMaterial).opacity = 0.9;
		}

		this.handleBox3Helper = new Box3Helper(this.handleBox3, 0xff0000);

		if (this.renderer.isDev) this.renderer.scene.add(this.handleBox3Helper);

		this.renderer.cameraController.centerCamera();
	}
}
